import { useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useProcedureResult } from '@americanexpress/iguazu-rpc-hooks';
import { useLanguagePack } from '@americanexpress/one-app-hooks';
import {
  getSelectedProductRegistry,
} from '@americanexpress/product-ducks';

import { getRootModuleName, getUserRoles } from '../selectors';
import formatLinks from '../utils/formatLinks';

export type UseLinksResponse = {
  isLoading: boolean;
  data?: ProductNavigationLinks,
  error?: Error,
};

const useLinks = ({
  locale,
  view,
}: ConsumerNavigationPropsFull, isConsumerNavEligible: boolean): UseLinksResponse => {
  const isProductView = view === 'PRODUCT';
  const rootModuleName = useSelector(getRootModuleName);
  const {
    opaqueAccountId,
    type: productType,
  } = useSelector(getSelectedProductRegistry, shallowEqual) || {};
  const roles = useSelector(getUserRoles);

  const procedureArgs = useMemo(() => ({
    procedureName: 'readProductNavigationLinksV1',
    args: {
      body: {
        opaqueAccountId,
        productType,
        locale,
        roles,
      },
    },
  }), [opaqueAccountId, productType, locale, roles]);

  const {
    isLoading: isNavigationLinksLoading,
    error: navigationLinksError,
    data: navigationLinks,
  } = useProcedureResult(procedureArgs, {
    defer: !isProductView || !opaqueAccountId || !isConsumerNavEligible,
  });

  const {
    isLoading: isLangLoading,
    error: langError,
    data: lang,
  } = useLanguagePack('axp-consumer-navigation', {
    defer: isProductView || !isConsumerNavEligible,
  });

  const fallbackLinks = useMemo(
    () => (productType ? lang?.links?.PRODUCT[productType] : []),
    [productType, lang?.links?.PRODUCT]
  );
  const accountLinks: ProductNavigationLinks = useMemo(() => {
    const baseLinks = lang?.links?.ACCOUNT || [];
    if (roles[0]?.includes('AUTHORIZED_MANAGER')) {
      return baseLinks.filter(({ path }: ProductNavigationLink) => path !== '/customer/profile');
    }
    return baseLinks;
  }, [lang?.links?.ACCOUNT, roles]);

  return useMemo(() => (isProductView && !navigationLinksError
    ? {
      isLoading: isNavigationLinksLoading,
      error: navigationLinksError,
      data: formatLinks(navigationLinks || [], rootModuleName),
    }
    : {
      isLoading: isLangLoading,
      error: langError,
      data: formatLinks((!isProductView ? accountLinks : fallbackLinks) || [], rootModuleName),
    }
  ), [
    isLangLoading,
    langError,
    isNavigationLinksLoading,
    navigationLinksError,
    navigationLinks,
    isProductView,
    accountLinks,
    fallbackLinks,
    rootModuleName,
  ]);
};

export default useLinks;
